import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import MVCard from '@/components/base/MVCard';
import { getMVById, getRelatedMVs, categories } from '@/mocks/mvs';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const mvId = parseInt(id || '0', 10);
  const mv = getMVById(mvId);
  const relatedMVs = mv ? getRelatedMVs(mv.id, mv.category, 4) : [];
  const category = mv ? categories.find((c) => c.slug === mv.category) : null;

  if (!mv) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-950 mb-2">MV未找到</h2>
            <p className="text-foreground-500 mb-4">该MV可能已被移除或链接无效</p>
            <Link
              to="/"
              className="inline-block px-6 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${mv.title} - ${mv.artist} MV | 欧美MV大全`,
    description: mv.description,
    image: mv.coverImage,
    datePublished: mv.releaseDate,
    author: {
      '@type': 'Person',
      name: mv.artist,
    },
    publisher: {
      '@type': 'Organization',
      name: '欧美MV大全',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${mv.title}是谁唱的？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${mv.title}由${mv.artist}演唱，是一首广受欢迎的欧美音乐作品。`,
        },
      },
      {
        '@type': 'Question',
        name: `${mv.title}属于什么音乐风格？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${mv.title}属于${category?.name || '流行'}风格，体现了该风格的音乐特色。`,
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: `${category?.name || ''}MV`, path: `/category/${mv.category}` },
              { label: `${mv.title} - ${mv.artist}` },
            ]}
          />

          <article className="w-full px-4 md:px-6 py-6 md:py-10">
            <div className="max-w-4xl mx-auto">
              <div className="relative aspect-video rounded-xl overflow-hidden mb-8">
                <img
                  src={mv.coverImage}
                  alt={`${mv.title} - ${mv.artist} MV`}
                  title={`${mv.title} - ${mv.artist}`}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-primary-500/90 flex items-center justify-center hover:bg-primary-500 transition-colors cursor-pointer hover:scale-110 transition-transform duration-300">
                    <i className="ri-play-fill text-3xl text-white ml-1"></i>
                  </div>
                </div>
              </div>

              <header className="mb-8">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Link
                    to={`/category/${mv.category}`}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap"
                  >
                    {category?.name}
                  </Link>
                  {mv.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-background-100 text-foreground-500 text-xs whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
                  {mv.title}
                </h1>
                <p className="text-lg md:text-xl text-foreground-700 mb-4">{mv.artist}</p>
                <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    发布: {mv.releaseDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    时长: {mv.duration}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    播放: {mv.views}
                  </span>
                </div>
              </header>

              <section className="prose max-w-none mb-10">
                <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-3">MV介绍</h2>
                <p className="text-foreground-700 leading-relaxed text-base">{mv.description}</p>
              </section>

              {relatedMVs.length > 0 && (
                <section className="border-t border-background-200 pt-8">
                  <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-6">
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-product-shop>
                    {relatedMVs.map((related) => (
                      <MVCard key={related.id} mv={related} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}